import { useFindCrItems } from '@/components/CrItemsSelect/Provider';
import { useFindCrSubItems } from '@/components/CrSubItemsSelect/Provider';
import { useFindCurrencies } from '@/components/CurrenciesSelect/Provider';
import { useFindDepts } from '@/components/DeptSelect/Provider';
import { useFindExcise } from '@/components/ExciseSelect/Provider';
import { useFindExItems } from '@/components/ExItemSelect/Provider';
import { useFindProjectCodes } from '@/components/ProjectSelect/Provider';
import { useGetCrItemsAllSuspense } from '@/context/services/cr_items/CrItems.service';
import { useGetCrSubItemsAllSuspense } from '@/context/services/cr_sub_items/CrSubItems.service';
import { useGetCurrencyUnitsAllSuspense } from '@/context/services/currencies/Currencies.service';
import { useGetDeptsAllSuspense } from '@/context/services/dept/Dept.service';
import { useGetExItemsAllSuspense } from '@/context/services/ex_item/ExItem.service';
import { useGetExcisesAllSuspense } from '@/context/services/excise/Excise.service';
import { useGetProjectCodesAllSuspense } from '@/context/services/project_code/ProjectCode.service';
import { useFindPulldownOption } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/CustomTransactionItems/PulldownType/useFindPulldownOption';

import {
  InvoiceTransactionMapping,
  exportMappings,
} from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/importExportMapping';
import {
  InvoiceTransaction,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import {
  exportsUrl,
  exportsWorkBook,
} from '@moneyforward/csv-excel-imp-exp-util';
import { toISODateString, toISODateStringYearMonth } from 'date-util';
import { isDayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';

export type ExportableCommonValue = {
  dealDate: string;
  name: string;
  jpyRate: string;
  quantity: string;
  withholdingIncomeTax: string;
  memo: string;
  currency: string;
};

export type InvoiceTransactionAdditionalValue = {
  exItemName: string;
  exciseName: string;
  deptName: string;
  projectName: string;
  crItemName: string;
  crSubItemName: string;
  tax: string; // correspond to exciseValue
  unitPriceExcludingTax: string; // correspond to unit
  unitPriceIncludingTax: string; // correspond to taxIncludedUnit
  totalValueExcludingTax: string; // correspond to totalValue
  totalValueIncludingTax: string; // correspond to taxIncludedTotalValue
};

export type InvoiceTransactionCustomValue = {
  [key: string]: string;
};

export type ExportableInvoiceTransactionType = ExportableCommonValue &
  InvoiceTransactionAdditionalValue &
  InvoiceTransactionCustomValue;

const exportableCommonValue: ExportableCommonValue = {
  dealDate: '',
  name: '',
  quantity: '',
  withholdingIncomeTax: '',
  memo: '',
  currency: '',
  jpyRate: '',
};

const additionalValue: InvoiceTransactionAdditionalValue = {
  exItemName: '',
  exciseName: '',
  deptName: '',
  projectName: '',
  crItemName: '',
  crSubItemName: '',
  tax: '',
  unitPriceExcludingTax: '',
  unitPriceIncludingTax: '',
  totalValueExcludingTax: '',
  totalValueIncludingTax: '',
};

export const exportableDefaultValue: ExportableInvoiceTransactionType =
  Object.assign({}, exportableCommonValue, additionalValue);

const useConvertFieldsToExportData = (
  transactionProperties: TransactionProperty[] = []
) => {
  const { findExciseById } = useFindExcise();
  const { findDeptById } = useFindDepts();
  const { findCrItemsById } = useFindCrItems();
  const { findCrSubItemsById } = useFindCrSubItems();
  const { findExItemsById } = useFindExItems();
  const { findProjectCodesById } = useFindProjectCodes();
  const { findCurrenciesById } = useFindCurrencies();
  const { findPulldownOptionById } = useFindPulldownOption();
  const { t } = useTranslation();

  return useCallback(
    async (
      items: InvoiceTransaction[],
      isEmpty?: boolean
    ): Promise<InvoiceTransactionMapping[]> => {
      if (isEmpty) {
        return [exportableDefaultValue];
      }

      const customizableProperties = transactionProperties.filter(
        (prop) => prop.type === 'custom'
      );

      return Promise.all(
        items.map(async (item) => {
          const excise = findExciseById(item.drExciseId);
          const dept = findDeptById(item.deptId);
          const crItem = findCrItemsById(item.crItemId);
          const crSubItem = findCrSubItemsById(item.crSubItemId);
          const project = findProjectCodesById(item.projectCodeId);
          const currency = findCurrenciesById(item.currency);
          const exItemName = findExItemsById(item.exItemId);

          const baseData: InvoiceTransactionMapping = {
            ...item,
            name: item.name ?? '',
            dealDate: item.dealDate ? toISODateString(item.dealDate) : '',
            exItemName: exItemName ?? '',
            quantity: item.quantity?.toString() ?? '',
            unitPriceExcludingTax: item.unit?.toString() ?? '',
            unitPriceIncludingTax: item.taxIncludedUnit.toString() ?? '',
            totalValueExcludingTax: item.totalValue?.toString() ?? '',
            totalValueIncludingTax: item.taxIncludedTotalValue.toString() ?? '',
            exciseName: excise ?? '',
            tax: item.exciseValue?.toString() ?? '',
            withholdingIncomeTax: item.withholdingIncomeTax?.toString() ?? '',
            deptName: dept ?? '',
            projectName: project ?? '',
            crItemName: crItem ?? '',
            crSubItemName: crSubItem ?? '',
            memo: item.memo ?? '',
            currency: currency ?? '',
            jpyRate: item.jpyRate?.toString() ?? '',
          };

          // Add custom transaction items
          const customItems: Record<string, string> = {};
          if (item.custom_transaction_items) {
            await Promise.all(
              customizableProperties.map(async (prop) => {
                const customValue = item.custom_transaction_items?.[prop.id];
                if (customValue === undefined || customValue === null) {
                  customItems[
                    `${t('invoice_transaction.column.customizable')}-${
                      prop.label
                    }`
                  ] = '';
                  return;
                }

                let exportValue: string;
                if (prop.componentType === 'pulldown') {
                  // Convert pulldown ID to label
                  exportValue =
                    (await findPulldownOptionById(
                      prop.id,
                      String(customValue)
                    )) || '';
                } else if (prop.componentType === 'date') {
                  // Convert Dayjs to ISO string
                  if (isDayjs(customValue)) {
                    if (prop.dateFormatType === 'year_month') {
                      exportValue = toISODateStringYearMonth(customValue) || '';
                    } else {
                      exportValue = toISODateString(customValue) || '';
                    }
                  } else {
                    exportValue = String(customValue) || '';
                  }
                } else {
                  // Convert other types to string
                  exportValue = String(customValue) || '';
                }

                customItems[
                  `${t('invoice_transaction.column.customizable')}-${
                    prop.label
                  }`
                ] = exportValue;
              })
            );
          } else {
            // Initialize empty custom items
            customizableProperties.forEach((prop) => {
              customItems[
                `${t('invoice_transaction.column.customizable')}-${prop.label}`
              ] = '';
            });
          }

          return {
            ...baseData,
            ...customItems,
          } satisfies InvoiceTransactionMapping;
        })
      );
    },
    [
      findCrItemsById,
      findCrSubItemsById,
      findCurrenciesById,
      findDeptById,
      findExItemsById,
      findExciseById,
      findProjectCodesById,
      findPulldownOptionById,
      transactionProperties,
      t,
    ]
  );
};

const useExportsCsv = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  isAdminOrApproval: boolean,
  hasWithholdingIncomeTax: boolean,
  canDisplayDrExcise: boolean,
  transactionProperties: TransactionProperty[] = []
) => {
  const { t } = useTranslation();
  return useCallback(
    async (
      fields: (InvoiceTransaction | InvoiceTransactionMapping)[],
      encoding: 'utf-8' | 'sjis'
    ) => {
      const url = await exportsUrl({
        data: fields,
        mappingObject: exportMappings(
          isAdminOrApproval,
          invoiceTransactionDetails,
          taxInclude,
          hasWithholdingIncomeTax,
          canDisplayDrExcise,
          transactionProperties,
          t
        ),
        format: 'csv',
        encodeType: encoding,
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = '支払依頼.csv';
      a.click();
      a.remove();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    },
    [
      hasWithholdingIncomeTax,
      invoiceTransactionDetails,
      isAdminOrApproval,
      taxInclude,
      canDisplayDrExcise,
      transactionProperties,
      t,
    ]
  );
};

const useExportExcel = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  isAdminOrApproval: boolean,
  hasWithholdingIncomeTax: boolean,
  canDisplayDrExcise: boolean,
  transactionProperties: TransactionProperty[] = []
) => {
  const exportDropdown = useExportDropdownData();
  const { t } = useTranslation();
  return useCallback(
    async (fields: (InvoiceTransaction | InvoiceTransactionMapping)[]) => {
      const mapping = exportMappings(
        isAdminOrApproval,
        invoiceTransactionDetails,
        taxInclude,
        hasWithholdingIncomeTax,
        canDisplayDrExcise,
        transactionProperties,
        t
      );
      const wb = exportsWorkBook(fields, mapping);
      exportDropdown(wb, mapping, canDisplayDrExcise);
      const url = await exportsUrl({
        data: wb,
        format: 'xlsx',
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = '支払依頼.xlsx';
      a.click();
      a.remove();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    },
    [
      isAdminOrApproval,
      invoiceTransactionDetails,
      taxInclude,
      hasWithholdingIncomeTax,
      canDisplayDrExcise,
      transactionProperties,
      t,
      exportDropdown,
    ]
  );
};

type BaseExportArgs = {
  taxInclude: boolean;
  invoiceTransactionDetails: boolean;
  isAdminOrApproval: boolean;
  hasWithholdingIncomeTax: boolean;
  canDisplayDrExcise: boolean;
  transactionProperties?: TransactionProperty[];
};

type ExportArgs = {
  fields: InvoiceTransaction[];
} & BaseExportArgs;

export const useExportXlsx = ({
  fields,
  taxInclude,
  invoiceTransactionDetails,
  isAdminOrApproval,
  hasWithholdingIncomeTax,
  canDisplayDrExcise,
  transactionProperties = [],
}: ExportArgs) => {
  const convertFieldsToExportData = useConvertFieldsToExportData(
    transactionProperties
  );
  const onWrapExportsXlsx = useExportExcel(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );

  const onExportsXlsx = useCallback(
    async () =>
      await onWrapExportsXlsx(await convertFieldsToExportData(fields)),
    [convertFieldsToExportData, fields, onWrapExportsXlsx]
  );

  return {
    onExportsXlsx,
  };
};

export const useExportHeader = ({
  taxInclude,
  invoiceTransactionDetails,
  isAdminOrApproval,
  hasWithholdingIncomeTax,
  canDisplayDrExcise,
  transactionProperties = [],
}: BaseExportArgs) => {
  const { t } = useTranslation();

  const customizableProperties = useMemo(
    () => transactionProperties.filter((prop) => prop.type === 'custom'),
    [transactionProperties]
  );

  const headerValue = useMemo(() => {
    const value = { ...exportableDefaultValue };
    customizableProperties.forEach((prop) => {
      value[`${t('invoice_transaction.column.customizable')}-${prop.label}`] =
        '';
    });
    return value;
  }, [customizableProperties, t]);

  const onWrapExportsXlsx = useExportExcel(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );
  const onExportsXlsxWithOnlyHeader = useCallback(
    async () => await onWrapExportsXlsx([headerValue]),
    [onWrapExportsXlsx, headerValue]
  );

  const exportsCsv = useExportsCsv(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );

  const onExportsUtf8CsvOnlyHeader = useCallback(async () => {
    await exportsCsv([headerValue], 'utf-8');
  }, [exportsCsv, headerValue]);
  const onExportsSjisCsvOnlyHeader = useCallback(async () => {
    await exportsCsv([headerValue], 'sjis');
  }, [exportsCsv, headerValue]);

  return {
    onExportsUtf8CsvOnlyHeader,
    onExportsSjisCsvOnlyHeader,
    onExportsXlsxWithOnlyHeader,
  };
};

export const useExportCsv = ({
  fields,
  taxInclude,
  invoiceTransactionDetails,
  isAdminOrApproval,
  hasWithholdingIncomeTax,
  canDisplayDrExcise,
  transactionProperties = [],
}: ExportArgs) => {
  const convertFieldsToExportData = useConvertFieldsToExportData(
    transactionProperties
  );
  const exportsCsv = useExportsCsv(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );
  const onExportsCsv = useCallback(
    async (encoding: 'utf-8' | 'sjis') => {
      await exportsCsv(await convertFieldsToExportData(fields), encoding);
    },
    [exportsCsv, convertFieldsToExportData, fields]
  );
  const onExportsUtf8Csv = useCallback(async () => {
    onExportsCsv('utf-8');
  }, [onExportsCsv]);

  // NOTE: sjis export
  const onExportsSjisCsv = useCallback(async () => {
    onExportsCsv('sjis');
  }, [onExportsCsv]);

  return {
    onExportsUtf8Csv,
    onExportsSjisCsv,
  };
};

const indexToExcelColumn = (index: number): string => {
  let column = '';
  // 内部計算のため1始まりに変換
  index++;

  while (index > 0) {
    // 26進数のように計算（ただしExcelは1から始まる）
    const remainder = (index - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    index = Math.floor((index - 1) / 26);
  }

  return column;
};

const useExportDropdownData = () => {
  const exItems = useGetExItemsAllSuspense();
  const excisesItems = useGetExcisesAllSuspense();
  const deptItems = useGetDeptsAllSuspense();
  const prjItem = useGetProjectCodesAllSuspense();
  const crItems = useGetCrItemsAllSuspense();
  const crSubItems = useGetCrSubItemsAllSuspense();
  const currencyItems = useGetCurrencyUnitsAllSuspense();
  return useCallback(
    (
      wb: ReturnType<typeof exportsWorkBook>,
      mapping: Record<string, string>,
      canDisplayDrExcise: boolean
    ) => {
      const mappingKeys = Object.keys(mapping);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: (Record<string, any> & { name?: string; cell: string })[] = [
        { exItems, name: 'exItemName', cell: 'C' },
        { excisesItems, name: 'exciseName', cell: 'I' },
        { deptItems, name: 'deptName', cell: 'M' },
        { prjItem, name: 'projectName', cell: 'N' },
        { crItems, name: 'crItemName', cell: 'O' },
        { crSubItems, name: 'crSubItemName', cell: 'P' },
        { currencyItems, name: 'currency', cell: 'Q' },
      ].filter((item) => canDisplayDrExcise || item.name !== 'exciseName');
      items.forEach((item) => {
        const idx = mappingKeys.findIndex((key) => key === item.name);
        const cell = indexToExcelColumn(idx);
        item.cell = cell;
        delete item.name;
      });
      items.forEach((item) => {
        let sheetName: string = '';
        let values: unknown[] = [];
        let cell: string = '';
        Object.entries(item).forEach(([key, value]) => {
          if (key === 'cell') {
            cell = value;
          } else {
            sheetName = key;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            values = value.map((value: any) => ({
              // selectで出力しているのは基本 display_name なので display_name を最初にする
              name: value.display_name ?? value.long_name ?? value.name,
            }));
          }
        });
        const sh = wb.addWorksheet(sheetName);
        sh.columns = [
          {
            header: '名前',
            key: 'name',
          },
        ];
        sh.addRows(values);
        sh.state = 'veryHidden';
        //@ts-ignore
        wb.getWorksheet('sheet1')?.dataValidations.add(`${cell}2:${cell}9999`, {
          type: 'list',
          allowBlank: true,
          formulae: [`${sheetName}!$A$2:$A$${values.length + 1}`],
        });
      });
    },
    [
      crItems,
      crSubItems,
      currencyItems,
      deptItems,
      exItems,
      excisesItems,
      prjItem,
    ]
  );
};
